import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import { Formation, Skill } from '../types';
import { toInputDate, mediaUrl } from '../utils';
import { BACKEND_URL } from '../config';
import FormationForm, { FormationFormData } from '../components/forms/FormationForm';
import PageMeta from '../components/PageMeta';
import { FormSkeleton } from '../components/Skeletons';

export default function EditFormation() {
  const { strid } = useParams<{ strid: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isStudy, setIsStudy] = useState(true);
  const [initialData, setInitialData] = useState<
    Partial<FormationFormData> & {
      existingLogoUrl?: string | null;
      manualSkills?: Skill[];
    }
  >({});
  const [loaded, setLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!strid) return;
    const token = localStorage.getItem('token');
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

    const normalizeLinks = (links: { title: unknown; url: string }[]) =>
      (links || []).map(l => ({
        title: (l.title && typeof l.title === 'object')
          ? (l.title as Record<string, string>)
          : { fr: String(l.title || ''), en: String(l.title || '') },
        url: l.url,
      }));

    Promise.all([
      fetch(`${BACKEND_URL}/api/formations/${strid}`, { headers })
        .then(r => { if (!r.ok) throw new Error(); return r.json() as Promise<Formation>; }),
      fetch(`${BACKEND_URL}/api/formations/assignation/${strid}`)
        .then(r => r.json() as Promise<Array<{ id: number; skills: Skill[] }>>)
        .catch(() => [] as Array<{ id: number; skills: Skill[] }>),
    ])
      .then(([f, assignedProjects]) => {
        setIsStudy(f.is_study);

        const manualSkills = (f.skills || []).filter((s: Skill) => !s.from_project);
        const selectedProjectIds = assignedProjects.map(p => p.id);

        setInitialData({
          name: f.name,
          localisation: f.localisation,
          isStudy: f.is_study,
          startDate: toInputDate(f.start_date),
          endDate: f.end_date ? toInputDate(f.end_date) : '',
          isOngoing: f.ongoing,
          type: f.type || { fr: '', en: '' },
          degree: f.degree || { fr: '', en: '' },
          position: f.position || { fr: '', en: '' },
          courses: f.courses || { fr: '', en: '' },
          tasks: f.tasks || { fr: '', en: '' },
          manualSkillIds: manualSkills.map(s => s.id),
          selectedProjectIds,
          privateLinks: normalizeLinks((f.private_links || []) as { title: unknown; url: string }[]),
          existingLogoUrl: f.logo
            ? (mediaUrl(f.logo, `${BACKEND_URL}/formations/${strid}`) ?? null)
            : null,
          manualSkills,
        });
        setLoaded(true);
      })
      .catch(() => {
        toast.error(String(t('error_loading_formation')));
        navigate(-1);
      });
  }, [strid, t, navigate]);

  const handleSubmit = async (data: FormationFormData & { logoFile: File | null }) => {
    const token = localStorage.getItem('token');
    if (!token) return navigate(-1);
    setSubmitting(true);

    const formData = new FormData();
    formData.append('isStudy', String(data.isStudy));
    formData.append('name', data.name);
    formData.append('localisation', data.localisation);
    formData.append('position', JSON.stringify(data.position));
    formData.append('tasks', JSON.stringify(data.tasks));
    formData.append('courses', JSON.stringify(data.courses));
    formData.append('start_date', data.startDate);
    formData.append('end_date', data.isOngoing ? 'null' : data.endDate);
    formData.append('ongoing', String(data.isOngoing));
    formData.append('degree', JSON.stringify(data.degree));
    formData.append('type', JSON.stringify(data.type));
    formData.append('private_links', JSON.stringify(data.privateLinks.filter(l => l.url.trim())));
    data.manualSkillIds.forEach((id, i) => formData.append(`skills[${i}]`, String(id)));
    data.selectedProjectIds.forEach((id, i) => formData.append(`projects[${i}]`, String(id)));
    if (data.logoFile) formData.append('logo', data.logoFile);

    try {
      const res = await fetch(`${BACKEND_URL}/api/formations/${strid}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) navigate('/formations', { replace: true });
      else toast.error(String(t('error_saving')));
    } catch {
      toast.error(String(t('error_saving')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative z-10 py-6 sm:py-10 px-4 pt-24">
      <PageMeta title={t('edit_formation') as string} />
      <div className="max-w-6xl mx-auto">
        <Link
          to="/formations"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-primary-light transition-colors mb-5 group"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
          {t('formations')}
        </Link>
        <h1 className="font-display font-extrabold tracking-[-0.03em] text-4xl text-ink mb-8">{t('edit_formation')}</h1>

        {loaded ? (
          <FormationForm
            mode="edit"
            isStudy={isStudy}
            initialData={initialData}
            onSubmit={handleSubmit}
            submitting={submitting}
          />
        ) : (
          <FormSkeleton />
        )}
      </div>
    </div>
  );
}
